"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useWebSocket } from "@/hooks/use-websocket";
import type { WorkspaceWebSocket } from "@/lib/ws";
import { usePathname } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";
import { api } from "@/lib/api";
import { getPageContext } from "@/lib/page-context";
import { usePageContextRegistry } from "@/contexts/page-context-registry";
import { useProject } from "@/contexts/project-context";
import type { ChatMessage } from "@/components/chat/message-list";
import type { Agent, Conversation, ToolCallLog, ModelDowngradeInfo, Message, UngroundedInfo } from "@/lib/types";
import type { SentAttachment } from "@/components/chat/chat-input";
import { useAutoSpeak } from "@/lib/speech";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DelegationState {
  agentName: string;
  task: string;
  active: boolean;
}

export interface AgentTask {
  agentName: string;
  task: string;
  status: "working" | "complete" | "error";
  progress?: number;
}

interface ChatContextValue {
  // Connection
  connected: boolean;
  agents: Agent[];
  activeAgent: Agent | null;
  availableAgents: Agent[];

  // Conversation state (persists across navigation)
  messages: ChatMessage[];
  conversations: Conversation[];
  activeConvId: string | undefined;
  isTyping: boolean;
  restoring: boolean;
  delegation: DelegationState | null;
  activeToolName: string | undefined;
  backgroundTasks: AgentTask[];
  lastMessages: Record<string, string>;

  // Actions
  sendChat: (text: string, attachments?: SentAttachment[]) => void;
  selectConversation: (id: string) => void;
  startNewConversation: () => void;
  clearMessages: () => void;
  /** Soft-archive a 1:1 conversation. If the deleted conversation is the
   *  currently-active one, navigates to /chat (no selection). Spec:
   *  ctrl-a_workspace-main/docs/superpowers/specs/2026-05-04-room-chat-archive-and-projects-contacts-design.md. */
  deleteConversation: (id: string) => Promise<void>;
  /** Manual refetch of the conversations list. Called from
   *  useEntitySubscription('conversation') so external mutations (other tabs,
   *  agent tools) flow back into the sidebar without a page reload. */
  refetchConversations: () => Promise<void>;
  setDelegation: (d: DelegationState | null) => void;
  setBackgroundTasks: React.Dispatch<React.SetStateAction<AgentTask[]>>;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setIsTyping: React.Dispatch<React.SetStateAction<boolean>>;
  setActiveToolName: React.Dispatch<React.SetStateAction<string | undefined>>;
  /** Abort the in-flight chat turn on the server. Called by the Cancel
   *  button in the ThinkingIndicator timeout panel. Server responds with
   *  a turn-cancelled event that finalizes the UI state. */
  cancelCurrentTurn: () => void;
  /** Hand off the in-flight chat turn to a background_tasks row. Called by
   *  the Background button (and the 10-min auto-fire). Server responds with
   *  turn-backgrounded (success, includes task_id) or
   *  turn-backgrounded-failed (trial session, no active turn, server
   *  error). UI updates its system message based on which arrives. */
  backgroundCurrentTurn: () => void;

  // Raw WS for advanced usage
  nudgeAgent: (text: string) => void;
  /** Replay the last user message so the agent re-runs the turn. Used by
   *  the inline approval UX: after approve/deny, the card calls this so
   *  GATE 3 finds the freshly-resolved decision and the tool executes.
   *  No-op if there's no prior user message. */
  retryLastUserTurn: () => void;
  /** Raw WS instance for surfaces that need to subscribe to room frames
   *  or send join-room/leave-room. Null until the WS first connects. */
  ws: WorkspaceWebSocket | null;
}

/**
 * Map a persisted row to the in-memory ChatMessage shape.
 *
 * ONE mapper for both rehydration paths (first-mount restore and
 * selectConversation). They were duplicated and had already drifted — which
 * is part of why the rehydrated turn stayed a five-field skeleton while the
 * live WebSocket path built twelve.
 *
 * `pendingDecisions` is deliberately NOT rehydrated: those are live approval
 * state, and re-rendering Allow/Deny for an already-resolved decision is
 * worse than showing nothing.
 */
function rowToChatMessage(m: Message, fallbackAgentName: string): ChatMessage {
  const meta = m.metadata;
  return {
    id: String(m.id),
    role: m.role as "user" | "assistant" | "system",
    content: m.content,
    timestamp: new Date(m.created_at),
    // Prefer the RECORDED author. Falling back to the currently-selected
    // agent silently relabels every past turn in a multi-agent conversation.
    agentName: meta?.agentName ?? fallbackAgentName,
    toolCalls: meta?.toolCalls,
    thinking: meta?.thinking,
    inputTokens: meta?.inputTokens,
    outputTokens: meta?.outputTokens,
    sources: meta?.sources,
    modelDowngraded: meta?.modelDowngraded,
    // Parsed by the route but previously dropped here, so a tool list the
    // budget had shortened rendered as if it were complete.
    droppedToolCalls: meta?.droppedToolCalls,
  };
}

function genId() {
  return Math.random().toString(36).slice(2);
}

/**
 * A visible, honest stand-in for history we could not load.
 *
 * The distinction this preserves is the whole point of the messages route
 * returning 500 rather than `200 []`: "this conversation is empty" and "we
 * could not read this conversation" look identical to a user, and only one of
 * them means their work is gone. Silence defaults them to the wrong one.
 */
function loadFailureMessage(err: unknown): ChatMessage {
  const e = err as { code?: string; message?: string } | undefined;
  const known = e?.code === "MESSAGES_LOAD_FAILED";
  return {
    id: genId(),
    role: "system",
    content: known
      ? "Couldn't load this conversation's history. Your messages are still saved — refresh to try again."
      : `Couldn't load this conversation's history: ${e?.message ?? "unknown error"}`,
    timestamp: new Date(),
  };
}

// Last-wins dedupe by id. The messages endpoint should only ever return
// unique rows (id is PK on the messages table) but a duplicate-keys warning
// fired in the wild on 2026-05-02 — backstop here so the UI doesn't render
// two MessageBubbles for the same DB row regardless of upstream shape.
function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Map<string, T>();
  for (const item of items) seen.set(item.id, item);
  return Array.from(seen.values());
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ChatContext = createContext<ChatContextValue | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatContext must be used within ChatProvider");
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider — lives in workspace layout, never unmounts during navigation
// ---------------------------------------------------------------------------

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { connected, agents, sendMessage, selectAgent, cancelTurn, backgroundTurn, onResponse, onTyping, onToolActivity, ws } =
    useWebSocket();

  const pathname = usePathname();
  const { user: authUser, loading: authLoading } = useAuth();
  const pageContextRegistry = usePageContextRegistry();
  // Project-aware context — every chat send prepends [Active project: X]
  // so agents know which project the user is working in. Wired via
  // ProjectProvider (already mounted in the workspace layout). When no
  // project is selected, the line is omitted (no clutter).
  const { currentProject } = useProject();
  const activeProjectRef = useMemo(
    () => currentProject ? {
      id: currentProject.id,
      name: currentProject.name,
      description: currentProject.description ?? null,
    } : null,
    [currentProject],
  );

  // ── Core state (persists because this provider never unmounts) ──────────
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  // Distinguish "not loaded yet" from "loaded, empty". The onboarding-agent
  // routing below only fires after the first successful conversations fetch
  // so returning users aren't briefly routed to onboarding while the list
  // loads.
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [activeConvId, setActiveConvId] = useState<string | undefined>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("ctrl-a-active-conv") ?? undefined;
    }
    return undefined;
  });
  const [restoring, setRestoring] = useState(() => {
    if (typeof window !== "undefined") {
      return !!localStorage.getItem("ctrl-a-active-conv");
    }
    return false;
  });
  const [isTyping, setIsTyping] = useState(false);
  const [delegation, setDelegation] = useState<DelegationState | null>(null);
  const [activeToolName, setActiveToolName] = useState<string | undefined>();
  const [backgroundTasks, setBackgroundTasks] = useState<AgentTask[]>([]);
  const lastMessagesRef = useRef<Record<string, string>>({});
  // The text of the most recent user message we sent over WS. Kept so we can
  // replay it transparently if the server responds with
  // "No agent selected" — happens when the server has GC'd the session's
  // agent binding (e.g. after long idle) or when the client's auto-reconnect
  // path didn't re-fire `select-agent`. See WS error handler below.
  const lastSentChatRef = useRef<string>('');
  const recoveryAttemptedRef = useRef<boolean>(false);
  // Conv ID we sent the most recent chat frame against. The server may
  // create a new conv ID in its response (especially when our retry path
  // fires `select-agent` mid-flight), and the existing stale-routing check
  // would silently drop responses whose conv ID differs from the UI's
  // current `activeConvId`. We treat any response that arrives shortly
  // after a send AND in this expected-conv window as "for the current view"
  // even if conv IDs don't match exactly.
  const expectingResponseRef = useRef<boolean>(false);

  // ── Agent resolution ────────────────────────────────────────────────────
  const [apiAgents, setApiAgents] = useState<Agent[]>([]);
  useEffect(() => {
    if (agents.length === 0) {
      api.agents.list().then(setApiAgents).catch(() => {});
    }
  }, [agents.length]);

  const availableAgents = agents.length > 0 ? agents : apiAgents;
  // New-account onboarding routing. Prefer the authoritative flag on the
  // account row (account.onboarding_complete) — set to true only when the
  // onboarding-agent has explicitly called the complete_onboarding tool.
  // Fall back to the "zero conversations" heuristic if auth hasn't loaded
  // yet OR if the field is missing (older accounts that predate the column).
  // See docs/superpowers/specs/2026-04-21-signup-seam-design.md.
  const onboardingAgent =
    availableAgents.find((a) => a.id === "onboarding-agent") ?? null;
  const accountFlagSaysNewUser =
    !authLoading && authUser ? authUser.onboarding_complete !== true : undefined;
  const emptyInboxHeuristic = conversationsLoaded && conversations.length === 0;
  // While auth is still loading, isNewUser is undefined — we don't know
  // yet whether to route to the onboarding agent or the user's primary
  // agent. Letting the heuristic answer `true` for new accounts (zero
  // conversations) before auth resolves causes the visible swap from
  // standard → onboarding agent (or vice versa) on every chat mount.
  // Spec: docs/superpowers/specs/2026-05-09-auth-loading-flicker-fix-design.md
  const isNewUser = authLoading
    ? undefined
    : (accountFlagSaysNewUser ?? emptyInboxHeuristic);
  // Sticky default agent — kept for backwards-compat with conversations
  // bound to a non-CTRL-A agent (created before /chat dropdown removal
  // 2026-05-17). New conversations always bind to the account default.
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("ctrl-a-active-agent");
  });
  const userPickedAgent =
    selectedAgentId
      ? availableAgents.find((a) => a.id === selectedAgentId) ?? null
      : null;
  // Spec C: per-user Chief of Staff. For Team-tier users, the session
  // payload populates user.cos_agent_id; bind /chat to it by default so
  // each seat lands in THEIR conductor rather than the shared ctrl-a.
  // Falls through to availableAgents[0] if the CoS agent isn't yet in
  // the hired list (race between session-mint and agent-list refresh).
  const cosAgent =
    authUser?.cos_agent_id
      ? availableAgents.find((a) => a.id === authUser.cos_agent_id) ?? null
      : null;
  // When isNewUser is undefined (auth still loading) AND the user hasn't
  // explicitly picked an agent, return null instead of falling through to
  // availableAgents[0] — that's the swap source. Consumers render an
  // empty/skeleton chat shell when activeAgent is null, then re-render
  // with the resolved agent once auth completes. userPickedAgent is
  // derived from localStorage synchronously so it short-circuits this
  // path on every reload after the first.
  const activeAgent =
    userPickedAgent ??
    (isNewUser === undefined
      ? null
      : isNewUser && onboardingAgent
        ? onboardingAgent
        : cosAgent ?? availableAgents[0] ?? null);

  // ── Persist activeConvId to localStorage ────────────────────────────────
  useEffect(() => {
    if (activeConvId) {
      localStorage.setItem("ctrl-a-active-conv", activeConvId);
    } else {
      localStorage.removeItem("ctrl-a-active-conv");
    }
  }, [activeConvId]);

  // ── Restore conversation on first mount ─────────────────────────────────
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    // `?conv=<id>` query param wins over localStorage. This is how the
    // signup page hands off the seeded onboarding conversation so the new
    // user lands mid-context instead of on the blank default pane.
    // Spec: docs/superpowers/specs/2026-04-21-signup-seam-design.md.
    const urlConvId =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("conv") ?? undefined
        : undefined;

    const persistedId =
      typeof window !== "undefined"
        ? localStorage.getItem("ctrl-a-active-conv") ?? undefined
        : undefined;

    const restoreId = urlConvId || activeConvId || persistedId;

    api.chat
      .conversations()
      .then((convs) => {
        setConversations(convs);
        setConversationsLoaded(true);
        // Defense-in-depth: if our restored convId belongs to a previous
        // account (e.g. logout didn't fire cleanly, or the session was
        // forced to expire), it won't appear in this account's convs list.
        // Drop it before WS select-agent tries to resume it and gets
        // `WS select-agent rejected: conversation ownership mismatch`.
        // logout() clears the storage key directly; this guard catches
        // every other path where a stale convId could leak in.
        const candidateId = activeConvId ?? restoreId;
        if (candidateId && !convs.some((c) => c.id === candidateId)) {
          setActiveConvId(undefined);
          setMessages([]);
        }
      })
      .catch(() => {
        // Failure means we can't tell new-vs-returning; default to
        // "not a new user" so we don't incorrectly pin onboarding-agent on
        // a returning user whose conversations endpoint just flapped.
        setConversationsLoaded(true);
      });

    if (restoreId) {
      if (!activeConvId) setActiveConvId(restoreId);
      api.chat
        .messages(restoreId)
        .then((msgs) => {
          if (msgs.length > 0) {
            setMessages(
              dedupeById(
                msgs.map((m) =>
                  rowToChatMessage(m, activeAgent?.custom_name ?? activeAgent?.name ?? "Agent"),
                ),
              ),
            );
          }
        })
        .catch((err: Error) => {
          // Not swallowed. The route returns 500 with MESSAGES_LOAD_FAILED when
          // it cannot read the history, precisely so this is distinguishable
          // from an empty conversation — and an empty catch here threw that
          // distinction away, rendering a blank pane either way. A 500 with no
          // consumer is not a fix; it is the same blank conversation with a
          // different status code.
          setMessages((prev) => [...prev, loadFailureMessage(err)]);
        })
        .finally(() => setRestoring(false));
    } else {
      setRestoring(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Select agent on WS connect ──────────────────────────────────────────
  const convIdRef = useRef(activeConvId);
  useEffect(() => {
    convIdRef.current = activeConvId;
  }, [activeConvId]);

  useEffect(() => {
    if (connected && activeAgent) {
      selectAgent(activeAgent.id, convIdRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, activeAgent?.id]);

  // ── WebSocket: responses ────────────────────────────────────────────────
  useEffect(() => {
    const off = onResponse(
      (data: {
        content?: string;
        toolCalls?: ToolCallLog[];
        sources?: unknown[];
        thinking?: string;
        inputTokens?: number;
        outputTokens?: number;
        conversationId?: string;
        pendingDecisions?: Array<{
          id: string;
          tool_name: string;
          agent_id: string;
          description: string;
          created_at: string;
        }>;
        modelDowngraded?: ModelDowngradeInfo;
        ungrounded?: UngroundedInfo;
      }) => {
        // Stale-response routing (Phase 2 of long-turn-handoff spec).
        // If a response arrives for a conversation the user has already
        // navigated away from, DO NOT yank the UI back. Instead record
        // it as an ActivityStrip task they can click to jump to, and
        // leave the current conversation view untouched. The response's
        // message is also NOT appended to the current view — it belongs
        // to the other conversation's message list, which will fetch
        // fresh when the user navigates there.
        //
        // Carve-outs (added 2026-05-02 to fix silent drops):
        //   1. activeConvId is empty — first turn of a fresh conv. Server
        //      assigns a conv ID on response; before this fix, that ID
        //      didn't match `convIdRef.current` (undefined) and the
        //      response was incorrectly routed to background tasks.
        //   2. We're expecting a response right now (sendChat just fired
        //      and hasn't been answered yet). The recovery path can cause
        //      the server to issue a new conv ID even mid-conversation;
        //      the user is still looking at this thread, so honor it.
        const respondingTo = data.conversationId;
        const isForCurrentConv =
          !respondingTo ||
          respondingTo === convIdRef.current ||
          !convIdRef.current ||
          expectingResponseRef.current;

        if (!isForCurrentConv) {
          const convTitle =
            conversations.find((c) => c.id === respondingTo)?.title ??
            'another conversation';
          lastMessagesRef.current[respondingTo] = data.content ?? '';
          setBackgroundTasks((prev) => {
            const rowTitle = `Response ready: ${convTitle}`;
            if (prev.some((t) => t.task === rowTitle)) return prev;
            return [
              ...prev,
              {
                agentName: activeAgent?.custom_name ?? activeAgent?.name ?? 'Agent',
                task: rowTitle,
                status: 'complete',
              },
            ];
          });
          // Refresh the sidebar so the other conversation's last-message
          // preview updates even though the user hasn't navigated there.
          api.chat.conversations().then((convs) => setConversations(convs)).catch(() => {});
          return;
        }

        setIsTyping(false);
        setActiveToolName(undefined);
        expectingResponseRef.current = false;
        const msg: ChatMessage = {
          id: genId(),
          role: "assistant",
          content: data.content ?? "",
          toolCalls: data.toolCalls,
          sources: data.sources,
          thinking: data.thinking,
          inputTokens: data.inputTokens,
          outputTokens: data.outputTokens,
          timestamp: new Date(),
          agentName: activeAgent?.custom_name ?? activeAgent?.name ?? "Agent",
          // Approval decisions the agent opened this turn — renders as inline
          // Allow Once / Session / Always / Deny buttons below the message.
          pendingDecisions: data.pendingDecisions,
          // Prepaid-credits auto-downgrade note (2026-07-01 spec).
          modelDowngraded: data.modelDowngraded,
          // Grounding gate (audit F01): answer not verified against the
          // knowledge library. Bubble renders a caveat above the content.
          ungrounded: data.ungrounded,
        };
        setMessages((prev) => [...prev, msg]);

        if (data.conversationId) {
          const newId = data.conversationId;
          setActiveConvId((prev) => {
            // First time this conv ID is seen — refresh the sidebar so it
            // shows up. Cheap no-op when the user is just continuing an
            // existing thread.
            if (prev !== newId && !conversations.some((c) => c.id === newId)) {
              api.chat
                .conversations()
                .then((convs) => setConversations(convs))
                .catch(() => {});
            }
            return newId;
          });
          lastMessagesRef.current[newId] = data.content ?? "";
        }
      }
    );
    return off;
  }, [onResponse, activeAgent, conversations]);

  // ── WebSocket: agent-selected ───────────────────────────────────────────
  // Fires when the backend confirms a select-agent — either resuming an
  // existing conversation or (after startNewConversation) creating a fresh
  // one. Without this handler the new convId from a fresh conversation was
  // dropped until the first response came back, making the "+ New" button
  // feel like a no-op.
  useEffect(() => {
    if (!ws) return;
    const off = ws.on("agent-selected", (data: { conversationId?: string }) => {
      if (!data.conversationId) return;
      setActiveConvId(data.conversationId);
      // Refetch the conversations list so the new thread appears in the
      // sidebar without a page reload. Swallow errors — the sidebar just
      // stays stale in that case, no user-visible break.
      api.chat
        .conversations()
        .then((convs) => setConversations(convs))
        .catch(() => {});
    });
    return off;
  }, [ws]);

  // ── WebSocket: typing ───────────────────────────────────────────────────
  useEffect(() => {
    const off = onTyping((data: { typing?: boolean }) => {
      setIsTyping(data.typing !== false);
    });
    return off;
  }, [onTyping]);

  // ── WebSocket: tool activity ────────────────────────────────────────────
  useEffect(() => {
    const off = onToolActivity(
      (data: {
        tool?: string;
        agentName?: string;
        task?: string;
        done?: boolean;
        progress?: number;
        error?: boolean;
      }) => {
        if (data.tool && data.tool !== "delegate_task") {
          setActiveToolName(data.done ? undefined : data.tool);
        }

        if (data.tool === "delegate_task" || data.agentName) {
          const agentKey = data.agentName ?? "Agent";
          const taskDesc = data.task ?? data.tool ?? "a task";

          setDelegation({
            agentName: agentKey,
            task: taskDesc,
            active: !data.done,
          });

          setBackgroundTasks((prev) => {
            const existing = prev.findIndex((t) => t.agentName === agentKey);
            if (existing >= 0) {
              const updated = [...prev];
              updated[existing] = {
                ...updated[existing],
                status: data.error ? "error" : data.done ? "complete" : "working",
                progress: data.progress ?? updated[existing].progress,
              };
              return updated;
            }
            return [
              ...prev,
              {
                agentName: agentKey,
                task: taskDesc,
                status: data.error ? "error" : data.done ? "complete" : "working",
                progress: data.progress,
              },
            ];
          });
        }
      }
    );
    return off;
  }, [onToolActivity]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const sendChat = useCallback(
    (text: string, attachments?: SentAttachment[]) => {
      const hasAttachments = attachments && attachments.length > 0;
      if (!text.trim() && !hasAttachments) return;

      // Render attachment URLs in the user-visible message so the agent sees them.
      // Backend has no first-class attachments field yet — embed as a trailing block.
      const attachmentBlock = hasAttachments
        ? "\n\n[Attached files]\n" +
          attachments!
            .map((a) => `- ${a.name} (${a.mime || "file"}) ${a.url}`)
            .join("\n")
        : "";
      const composed = text.trim() + attachmentBlock;

      const userMsg: ChatMessage = {
        id: genId(),
        role: "user",
        content: composed,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsTyping(true);

      if (activeConvId) {
        lastMessagesRef.current[activeConvId] = composed;
      }

      // Resolve L2 snapshot from the registered provider for this route
      // (if any). Pages register via useRegisterPageContext.
      const snapshot = pageContextRegistry?.resolve(pathname) ?? null;
      const pageCtx = getPageContext(pathname, snapshot, activeProjectRef);
      if (connected) {
        lastSentChatRef.current = composed;
        recoveryAttemptedRef.current = false;
        expectingResponseRef.current = true;
        sendMessage(composed, activeConvId, { pageContext: pageCtx });
      } else if (activeAgent) {
        api.chat
          .send(activeAgent.id, composed, activeConvId)
          .then((result) => {
            setIsTyping(false);
            const agentMsg: ChatMessage = {
              id: genId(),
              role: "assistant",
              content: result.content,
              toolCalls: result.toolCalls,
              sources: result.sources as unknown[],
              timestamp: new Date(),
              agentName: activeAgent?.custom_name ?? activeAgent?.name ?? "Agent",
              modelDowngraded: result.modelDowngraded,
              ungrounded: result.ungrounded,
            };
            setMessages((prev) => [...prev, agentMsg]);
          })
          .catch((err: Error) => {
            setIsTyping(false);
            const errMsg: ChatMessage = {
              id: genId(),
              role: "system",
              content: `Error: ${err.message}`,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errMsg]);
          });
      }
    },
    [connected, sendMessage, activeAgent, activeConvId, pathname]
  );

  const selectConversation = useCallback(
    (id: string) => {
      setActiveConvId(id);
      setMessages([]);
      setIsTyping(false);
      if (activeAgent) {
        selectAgent(activeAgent.id, id);
      }
      api.chat
        .messages(id)
        .then((msgs) => {
          const mapped: ChatMessage[] = msgs.map((m) =>
            rowToChatMessage(m, activeAgent?.custom_name ?? activeAgent?.name ?? "Agent"),
          );
          setMessages(dedupeById(mapped));
        })
        .catch((err: Error) => {
          // setMessages([]) above already cleared the pane, so without this the
          // user sees an empty conversation that looks successfully loaded.
          setMessages([loadFailureMessage(err)]);
        });
    },
    [activeAgent, selectAgent]
  );

  const startNewConversation = useCallback(() => {
    setActiveConvId(undefined);
    setMessages([]);
    setIsTyping(false);
    setDelegation(null);
    setBackgroundTasks([]);
    setActiveToolName(undefined);
    if (activeAgent) {
      selectAgent(activeAgent.id);
    }
  }, [activeAgent, selectAgent]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const refetchConversations = useCallback(async () => {
    try {
      const convs = await api.chat.conversations();
      setConversations(convs);
    } catch {
      // Non-fatal — keep stale list rather than blank it on a transient error.
    }
  }, []);

  const deleteConversation = useCallback(
    async (id: string) => {
      // Optimistic: drop from local list immediately.
      setConversations((prev) => prev.filter((c) => c.id !== id));
      // If the deleted conversation is the currently-active one, drop the
      // selection so the chat page lands on /chat with no thread.
      setActiveConvId((prev) => {
        if (prev === id) {
          setMessages([]);
          setIsTyping(false);
          return undefined;
        }
        return prev;
      });
      try {
        await api.chat.deleteConversation(id);
      } catch {
        // On error, refetch to put it back in the sidebar.
      } finally {
        await refetchConversations();
      }
    },
    [refetchConversations],
  );

  const nudgeAgent = useCallback(
    (text: string) => {
      sendChat(text);
    },
    [sendChat]
  );

  // ── Long-turn handoff wrappers ──────────────────────────────────────────
  // Wire the Cancel / Background buttons (in ThinkingIndicator) to actually
  // talk to the server. The system-message update happens when the server
  // confirms via turn-cancelled / turn-backgrounded events, not here —
  // otherwise the UI lies about a hand-off that might fail.
  const cancelCurrentTurn = useCallback(() => {
    cancelTurn();
  }, [cancelTurn]);

  const backgroundCurrentTurn = useCallback(() => {
    backgroundTurn();
  }, [backgroundTurn]);

  // ── WebSocket: turn-cancelled / turn-backgrounded / turn-backgrounded-failed ─
  useEffect(() => {
    if (!ws) return;
    const offCancelled = ws.on(
      'turn-cancelled',
      (_data: { reason?: string; noop?: boolean }) => {
        setIsTyping(false);
        setActiveToolName(undefined);
        setMessages((prev) => [
          ...prev,
          {
            id: genId(),
            role: 'system',
            content: 'Cancelled. The current tool call finished, then the agent stopped.',
            timestamp: new Date(),
          },
        ]);
      },
    );
    const offBackgrounded = ws.on(
      'turn-backgrounded',
      (data: { task_id?: string; title?: string }) => {
        setIsTyping(false);
        setActiveToolName(undefined);
        const title = data.title ?? 'this task';
        setMessages((prev) => [
          ...prev,
          {
            id: genId(),
            role: 'system',
            content: `Moved "${title}" to the background. It's tracked on your Mission Control dashboard; you'll see the result there when it finishes.`,
            timestamp: new Date(),
          },
        ]);
        if (data.task_id && data.title) {
          setBackgroundTasks((prev) => {
            if (prev.some((t) => t.task === data.title)) return prev;
            return [
              ...prev,
              { agentName: activeAgent?.custom_name ?? activeAgent?.name ?? 'Agent', task: data.title!, status: 'working' },
            ];
          });
        }
      },
    );
    const offBackgroundFailed = ws.on(
      'turn-backgrounded-failed',
      (data: { reason?: string }) => {
        // Degrade gracefully — the WS hand-off didn't take, so the response
        // will still arrive via the normal chat stream. Say so honestly.
        const reason =
          data.reason === 'no_account'
            ? "couldn't move to background (trial sessions don't persist yet)"
            : data.reason === 'no_active_turn'
              ? "couldn't move to background (nothing running)"
              : "couldn't move to background (server error)";
        setMessages((prev) => [
          ...prev,
          {
            id: genId(),
            role: 'system',
            content: `Note: ${reason}. The response will still arrive in chat.`,
            timestamp: new Date(),
          },
        ]);
      },
    );
    return () => {
      offCancelled();
      offBackgrounded();
      offBackgroundFailed();
    };
  }, [ws, activeAgent]);

  // ── WebSocket: proactive re-announce on reconnect ───────────────────────
  // When the WS drops and auto-reconnects, the workspace-main session loses
  // its agent binding. Re-fire `select-agent` immediately on the
  // `reconnected` event so the next user message doesn't trip the
  // "No agent selected" path below. The reactive recovery handler stays
  // as a safety net for the race case where a chat frame races ahead of
  // the rebind, or where the session binding is GC'd mid-session without
  // a reconnect (Render idle).
  useEffect(() => {
    if (!ws) return;
    const offReconnected = ws.on('reconnected', () => {
      if (activeAgent) {
        selectAgent(activeAgent.id, convIdRef.current);
      }
    });
    return offReconnected;
  }, [ws, activeAgent, selectAgent]);

  // ── WebSocket: server-side errors (auto-recover from session-state loss) ─
  // The server emits `{ type: "error", message: "No agent selected. Send
  // select-agent first." }` when it can't find an agent bound to the WS
  // session. This happens when:
  //   - Render GC'd the session's agent binding after idle
  //   - The auto-reconnect path didn't re-fire `select-agent` (historical
  //     ws.ts bug 2026-05-02 — fixed: `reconnected` now emits BEFORE
  //     `reconnectAttempts` is reset to 0, and chat-context subscribes to
  //     it above for the proactive rebind)
  //   - A race put the chat frame on the wire before `select-agent` settled
  // Recover by re-sending `select-agent` and replaying the last chat once.
  // The recoveryAttemptedRef guard prevents an infinite loop if the retry
  // also fails.
  useEffect(() => {
    if (!ws) return;
    const offError = ws.on('error', (data: { message?: string }) => {
      const msg = data.message ?? '';
      const isAgentMissing = /no agent selected/i.test(msg);
      if (!isAgentMissing) return;
      if (recoveryAttemptedRef.current) {
        setIsTyping(false);
        return;
      }
      if (!activeAgent || !lastSentChatRef.current) {
        setIsTyping(false);
        return;
      }
      recoveryAttemptedRef.current = true;
      selectAgent(activeAgent.id, convIdRef.current);
      // Small delay so server registers the agent before the chat frame
      // arrives; the WS is ordered, so this is belt-and-suspenders only.
      setTimeout(() => {
        const snapshot = pageContextRegistry?.resolve(pathname) ?? null;
        const pageCtx = getPageContext(pathname, snapshot, activeProjectRef);
        sendMessage(lastSentChatRef.current, convIdRef.current, {
          pageContext: pageCtx,
        });
      }, 50);
    });
    return offError;
  }, [ws, activeAgent, selectAgent, sendMessage, pathname]);

  // ── Retry after inline approval ─────────────────────────────────────────
  // Looks at the current messages list in reverse, finds the last user turn,
  // and re-sends its content. The agent re-runs; GATE 3 finds the freshly-
  // resolved decision (or the new auto_approve rule from 'always') and the
  // tool call executes. Silent no-op if there's no prior user message.
  const retryLastUserTurn = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "user" && m.content && m.content.trim()) {
        sendChat(m.content);
        return;
      }
    }
  }, [messages, sendChat]);

  // ── Context value ───────────────────────────────────────────────────────

  // Auto-speak is owned HERE, at the transcript's source, not in the components
  // that render it. /chat's MessageList and the global chat drawer both render
  // this same `messages` array, so wiring it in each renderer speaks every reply
  // twice. Rooms own their own transcript and wire it separately.
  useAutoSpeak(messages, activeConvId ?? null);

  const value = useMemo<ChatContextValue>(
    () => ({
      connected,
      agents: availableAgents,
      activeAgent,
      availableAgents,
      messages,
      conversations,
      activeConvId,
      isTyping,
      restoring,
      delegation,
      activeToolName,
      backgroundTasks,
      lastMessages: lastMessagesRef.current,
      sendChat,
      selectConversation,
      startNewConversation,
      clearMessages,
      deleteConversation,
      refetchConversations,
      setDelegation,
      setBackgroundTasks,
      setMessages,
      setIsTyping,
      setActiveToolName,
      cancelCurrentTurn,
      backgroundCurrentTurn,
      nudgeAgent,
      retryLastUserTurn,
      ws,
    }),
    [
      connected,
      availableAgents,
      activeAgent,
      messages,
      conversations,
      activeConvId,
      isTyping,
      restoring,
      delegation,
      activeToolName,
      backgroundTasks,
      sendChat,
      selectConversation,
      startNewConversation,
      clearMessages,
      deleteConversation,
      refetchConversations,
      cancelCurrentTurn,
      retryLastUserTurn,
      backgroundCurrentTurn,
      nudgeAgent,
      ws,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
